/**
 * AJAX forms — Theme Blueprint
 * ---------------------------------------------------------------------------
 * Progressive enhancement for Shopify storefront forms (contact, newsletter,
 * blog comments …): intercepts submits on `form[data-ajax-form]`, posts via
 * fetch and renders the server-side success/error state inline — no redirect,
 * no page reload.
 *
 * Usage (see .claude/skills/frontend-form/SKILL.md for the full recipe):
 *
 *   {% form 'contact', id: 'ContactForm', data-ajax-form: 'true' %}
 *     …fields…
 *     {% render 'form-status', form: form %}
 *     <button type="submit">…</button>
 *   {% endform %}
 *
 * How it works:
 * - The submit handler sits on the form itself, not on `document`, and stops
 *   propagation. Shopify's own storefront-forms script (inline in
 *   content_for_header, so its document-level listener is registered before
 *   this file loads) force-submits any /contact form that carries no
 *   `form_key` natively — mid-fetch, that reads as an error message followed
 *   by a page reload. A listener on the form runs in the target phase, before
 *   any document-level one, and keeps the event from ever reaching it.
 * - Client-side validation first (checkValidity/reportValidity).
 * - Spam protection (hCaptcha) stays intact AND the submit stays AJAX. The
 *   captcha owns the submit event: it preventDefaults, solves the invisible
 *   challenge, writes h-captcha-response into the form and then calls what it
 *   captured as the form's `submit` when it bound. That capture prefers the
 *   instance property over the prototype, so this file assigns its own
 *   function there first (bindNativeSubmit) — the captcha then hands us the
 *   signed form and we fetch it. Only a tokenless fetch would be rejected.
 * - The response HTML is parsed; the matching form's [data-form-success] /
 *   [data-form-errors] blocks (rendered by the form-status snippet) are
 *   cloned into the live [data-form-status] region → localized, server-
 *   rendered messages without duplicating copy in JS.
 * - If Shopify redirects to /challenge (interactive bot check — cannot be
 *   completed via AJAX), the form falls back to a native submit so the
 *   visitor can solve the challenge and the message is not lost.
 *
 * Events: dispatches `form:success` / `form:error` (bubbling) on the form.
 * UI copy fallbacks come from the [data-theme-strings] block in theme.liquid.
 */
(function () {
  "use strict";

  var themeStrings = (function () {
    var el = document.querySelector("[data-theme-strings]");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  })();
  var t = themeStrings.form || {};

  function statusEl(form) {
    return form.querySelector("[data-form-status]");
  }

  function showText(form, text, type) {
    var status = statusEl(form);
    if (!status) return;
    var p = document.createElement("p");
    p.className = "form-status__message form-status__message--" + type;
    p.textContent = text;
    status.innerHTML = "";
    status.appendChild(p);
  }

  function setBusy(form, on) {
    // Zweimal aufziehen würde als Label „Wird gesendet …" sichern und den
    // echten Button-Text verlieren — beim Captcha-Pfad passiert genau das
    // (einmal im Submit-Handler, einmal in send()).
    if (on === form.hasAttribute("data-busy")) return;
    var button = form.querySelector('[type="submit"]');
    form.toggleAttribute("data-busy", on);
    form.setAttribute("aria-busy", on ? "true" : "false");
    if (!button) return;
    if (on) {
      button.dataset.restoreLabel = button.textContent;
      button.disabled = true;
      if (t.sending) button.textContent = t.sending;
    } else {
      button.disabled = false;
      if (button.dataset.restoreLabel) button.textContent = button.dataset.restoreLabel;
    }
  }

  function handleResponse(form, response, html) {
    var doc = new DOMParser().parseFromString(html, "text/html");
    // Scope to the same form in the response (forms need a unique id) so we
    // never pick up another form's messages on multi-form pages.
    var scope = (form.id && doc.getElementById(form.id)) || doc;
    var errors = scope.querySelector("[data-form-errors]");
    var success = scope.querySelector("[data-form-success]");
    var status = statusEl(form);

    if (errors) {
      if (status) {
        status.innerHTML = "";
        status.appendChild(document.importNode(errors, true));
      }
      form.dispatchEvent(new CustomEvent("form:error", { bubbles: true }));
      return;
    }
    if (success || response.ok) {
      if (success && status) {
        status.innerHTML = "";
        status.appendChild(document.importNode(success, true));
      } else {
        showText(form, t.success || "Thank you! Your message has been sent.", "success");
      }
      form.reset();
      form.dispatchEvent(new CustomEvent("form:success", { bubbles: true }));
      return;
    }
    showText(form, t.error || "Something went wrong. Please try again.", "error");
    form.dispatchEvent(new CustomEvent("form:error", { bubbles: true }));
  }

  function send(form) {
    setBusy(form, true);
    fetch(form.action, {
      method: "POST",
      // URL-encoded (not multipart/JSON) — the format Shopify's form
      // endpoints expect. Note: file inputs are not supported this way.
      body: new URLSearchParams(new FormData(form)),
      headers: { Accept: "text/html,application/xhtml+xml" }
    })
      .then(function (response) {
        if (response.url && response.url.indexOf("/challenge") !== -1) {
          // Interactive bot check required — only solvable on Shopify's
          // challenge page. Fall back to a native submit (fields are still
          // filled, the captcha token is still injected) so nothing is lost.
          if (t.challenge) showText(form, t.challenge, "info");
          // Nicht form.submit(): das ist inzwischen der Captcha-Executor und
          // liefe über unsere Übernahme wieder hier herein.
          HTMLFormElement.prototype.submit.call(form);
          return null;
        }
        return response.text().then(function (html) {
          handleResponse(form, response, html);
        });
      })
      .catch(function () {
        showText(form, t.error || "Something went wrong. Please try again.", "error");
        form.dispatchEvent(new CustomEvent("form:error", { bubbles: true }));
      })
      .finally(function () {
        setBusy(form, false);
      });
  }

  function handleSubmit(e) {
    var form = e.currentTarget;
    // stopImmediatePropagation, not stopPropagation: Shopify's hCaptcha
    // handler sits on this very form (registered after this one, since it
    // binds no earlier than DOMContentLoaded), and same-node listeners are
    // only stopped by the immediate variant. Without it a programmatic
    // submit of an invalid form would still be sent by the captcha executor.
    if (form.hasAttribute("data-busy")) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (!form.checkValidity()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      form.reportValidity();
      return;
    }
    // Bei aktivem Spam-Schutz gehört das Event dem Captcha: sein Listener
    // preventDefaultet selbst, löst die unsichtbare Challenge, setzt das Token
    // ins Formular und ruft dann das, was es beim Binden als „submit" gesichert
    // hat — dank bindForms() unsere Übernahme. Hier also nur den Busy-State
    // setzen und zurücktreten; ein eigener fetch ginge ohne Token raus.
    if (form.dataset.hcaptchaBound || form.dataset.recaptchaBound) {
      setBusy(form, true);
      return;
    }
    // Own the event: stopping it here keeps Shopify's document-level listener
    // (which would persist the fields and submit natively) out of the way.
    e.preventDefault();
    e.stopImmediatePropagation();
    // Deliberately no Shopify.captcha.protect() here: it only binds a captcha
    // to the form, it never executes it, so it would hand this fetch an empty
    // h-captcha-response and a 400. Forms Shopify wants protected are already
    // bound by its own script (at load via data-shopify-captcha, otherwise on
    // the first focus) and took the native branch above.
    send(form);
  }

  // Der Angelpunkt für spam-geschützte Formulare. Shopifys hCaptcha-Bundle
  // sichert sich beim Binden `"function" == typeof form.submit ? form.submit :
  // HTMLFormElement.prototype.submit` und ruft genau das auf, nachdem es das
  // Token gesetzt hat. Steht dort zu diesem Zeitpunkt bereits unsere Funktion,
  // bekommt sie das fertig signierte Formular — und schickt es per fetch statt
  // per Navigation. Deshalb muss das hier VOR dem Binden passieren: diese
  // Datei ist `defer`, läuft also vor DOMContentLoaded, das Captcha bindet
  // frühestens dort (bzw. beim ersten Fokus).
  function bindNativeSubmit(form) {
    form.submit = function () {
      send(form);
    };
  }

  function bindForms(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var forms = scope.querySelectorAll("form[data-ajax-form]");
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].dataset.ajaxFormBound) continue;
      forms[i].dataset.ajaxFormBound = "true";
      forms[i].addEventListener("submit", handleSubmit);
      bindNativeSubmit(forms[i]);
    }
  }

  // This file is deferred, so the document is parsed by now.
  bindForms(document);

  // The theme editor re-renders sections without a page load.
  document.addEventListener("shopify:section:load", function (e) {
    bindForms(e.target);
  });
})();
